import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Add16 from '@carbon/icons-react/es/add/16';
import { Button } from '@rocketlane/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Checkbox } from 'components/atoms/checkbox';
import { Input } from 'components/facade/input';
import { SettingsControlPanel } from 'components/molecules/settings/control-panel';
import { toastService } from 'components/molecules/toast';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useCreateCreditNoteReasonMutation, useGetCreditNoteReasonsQuery, CREDIT_NOTE_REASONS_QUERY_KEY, useUpdateCreditNoteReasonMutation } from 'queries/credit-note-settings';
import { useUpdateSettings } from '../use-feature-setting';
import { CheckBoxWrap, ReasonCodeTable, ReasonCodeWrapper, SettingsLineWrapper } from './components';
import { ReasonCodeRenderer, StatusRenderer } from './grid-utils';
export const ReasonCodeSettings = ({ reasonMandatory: defaultReasonMandatory })=>{
    const [reasonMandatory, setReasonMandatory] = useState(defaultReasonMandatory);
    const [reasonCode, setReasonCode] = useState('');
    const [update] = useUpdateSettings();
    const queryClient = useQueryClient();
    const { mutateAsync: createReason } = useCreateCreditNoteReasonMutation();
    const { mutateAsync: updateReason } = useUpdateCreditNoteReasonMutation();
    const label = useTranslation('settings.credit_notes.reason_code');
    const translate = useTranslate();
    const { data: reasons, isLoading } = useGetCreditNoteReasonsQuery();
    const handleUpdateStatus = useCallback(async (reasonId, active)=>{
        try {
            await updateReason({
                reasonId,
                payload: {
                    active
                }
            }).then(()=>{
                queryClient.invalidateQueries(CREDIT_NOTE_REASONS_QUERY_KEY);
            });
        } catch (_error) {
            toastService({
                title: translate('error'),
                message: translate('settings.credit_notes.reason_code.update.error')
            }).error();
        }
    }, [
        updateReason,
        translate,
        queryClient
    ]);
    const tableColumns = useMemo(()=>[
            {
                title: `${translate('settings.credit_notes.reason_code.header')} (${(reasons === null || reasons === void 0 ? void 0 : reasons.length) || 0})`,
                key: 'reason',
                render: (_value, record)=>/*#__PURE__*/ _jsx(ReasonCodeRenderer, {
                        data: record
                    })
            },
            {
                title: translate('taxCode.table.header.status'),
                key: 'active',
                width: '30%',
                render: (_value, record)=>/*#__PURE__*/ _jsx(StatusRenderer, {
                        data: record,
                        onUpdateStatus: handleUpdateStatus
                    })
            }
        ], [
        translate,
        reasons === null || reasons === void 0 ? void 0 : reasons.length,
        handleUpdateStatus
    ]);
    const handleCreateReason = async ()=>{
        try {
            await createReason({
                reason: reasonCode,
                active: true
            }).then(()=>{
                setReasonCode('');
                queryClient.invalidateQueries(CREDIT_NOTE_REASONS_QUERY_KEY);
            });
        } catch (_error) {
            toastService({
                title: translate('error'),
                message: translate('settings.credit_notes.reason_code.create.error')
            }).error();
        }
    };
    return /*#__PURE__*/ _jsxs(SettingsControlPanel, {
        label: label,
        children: [
            /*#__PURE__*/ _jsx(CheckBoxWrap, {
                children: /*#__PURE__*/ _jsx(Checkbox, {
                    checked: reasonMandatory,
                    disabled: (reasons === null || reasons === void 0 ? void 0 : reasons.length) === 0 || isLoading,
                    onChange: (e)=>{
                        setReasonMandatory(e.target.checked);
                        update({
                            payload: {
                                reasonMandatory: e.target.checked
                            },
                            onError: ()=>{
                                setReasonMandatory(!e.target.checked);
                            }
                        });
                    },
                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                        id: "settings.credit_notes.reason_code.checkbox"
                    })
                })
            }),
            /*#__PURE__*/ _jsxs(SettingsLineWrapper, {
                children: [
                    /*#__PURE__*/ _jsx(Input, {
                        value: reasonCode,
                        onChange: (e)=>setReasonCode(e.target.value),
                        placeholder: useTranslation('settings.credit_notes.reason_code.placeholder'),
                        maxLength: 255
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        disabled: reasonCode.length === 0,
                        onClick: handleCreateReason,
                        variant: "primary",
                        type: "button",
                        leftIcon: /*#__PURE__*/ _jsx(Add16, {}),
                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "settings.credit_notes.reason_code.save"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ReasonCodeWrapper, {
                children: /*#__PURE__*/ _jsx(ReasonCodeTable, {
                    size: "small",
                    loading: isLoading,
                    dataSource: reasons || [],
                    pagination: false,
                    columns: tableColumns
                })
            })
        ]
    });
};
